import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminPricing = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    description: '',
    options: [],
    footer_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/pricing`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/pricing`, { ...content, id: 'pricing' }, getAuthHeaders());
      toast.success('Pricing saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...content.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setContent(prev => ({ ...prev, options: newOptions }));
  };

  const handleFeatureChange = (optionIndex, featureIndex, value) => {
    const newOptions = [...content.options];
    newOptions[optionIndex].features[featureIndex] = value;
    setContent(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setContent(prev => ({
      ...prev,
      options: [...prev.options, { title: '', duration: '', price: '', features: [''] }]
    }));
  };

  const removeOption = (index) => {
    setContent(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const addFeature = (optionIndex) => {
    const newOptions = [...content.options];
    newOptions[optionIndex].features.push('');
    setContent(prev => ({ ...prev, options: newOptions }));
  };

  const removeFeature = (optionIndex, featureIndex) => {
    const newOptions = [...content.options];
    newOptions[optionIndex].features = newOptions[optionIndex].features.filter((_, i) => i !== featureIndex);
    setContent(prev => ({ ...prev, options: newOptions }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-pricing">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Pricing</h1>
          <p className="text-muted-foreground">Manage lesson prices and packages</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-pricing-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-border p-6 space-y-4">
          <h2 className="font-semibold">Section Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <input
                type="text"
                value={content.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Footer Text</label>
            <input
              type="text"
              value={content.footer_text}
              onChange={(e) => handleChange('footer_text', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Pricing Options</h2>
            <Button variant="outline" size="sm" onClick={addOption}>
              <Plus className="w-4 h-4 mr-1" /> Add Option
            </Button>
          </div>
          
          <div className="space-y-6">
            {content.options.map((option, optionIndex) => (
              <div key={optionIndex} className="border border-border rounded-lg p-4 relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => removeOption(optionIndex)} 
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Title</label>
                    <input
                      type="text"
                      value={option.title}
                      onChange={(e) => handleOptionChange(optionIndex, 'title', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., Under 12's"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Duration</label>
                    <input
                      type="text"
                      value={option.duration}
                      onChange={(e) => handleOptionChange(optionIndex, 'duration', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., 45 minutes"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Price</label>
                    <input
                      type="text"
                      value={option.price}
                      onChange={(e) => handleOptionChange(optionIndex, 'price', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., £35"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium">Features</label>
                    <Button variant="ghost" size="sm" onClick={() => addFeature(optionIndex)} className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {option.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureChange(optionIndex, featureIndex, e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                          placeholder="Feature..."
                        />
                        <Button variant="ghost" size="icon" onClick={() => removeFeature(optionIndex, featureIndex)} className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
